import { useFormik } from 'formik'
import { FC, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Button,
  FetchState,
  FormInput,
  FormSelect,
  IOption,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import { RiscoAsoDtoCreate, RiscoAsoDtoUpdate, RiscoAsoView } from '../../core/_models'
import {
  ButtonType,
  FatoresRiscosLabels,
  FatorRiscoType,
  OptionsHelper,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import { createRisco, editRisco } from '../../core/_request'
import { riscoSchema } from './validation/riscoSchema'
import { useT } from '_metronic/i18n/Metronici18n'

export const RiscoClienteModal: FC = () => {
  const { fetchStates, closeModal } = useModal()
  const { t } = useT()

  const { data: risco } = (fetchStates['risco'] || {}) as FetchState<RiscoAsoView>

  const [searchParams] = useSearchParams()

  const idCliente = Number(searchParams.get('cliente'))
  const idEmpresa = Number(searchParams.get('empresa'))

  const initialValues: {
    setor: IOption<number> | undefined
    cargo: IOption<number> | undefined
    fatorRisco: IOption<FatorRiscoType> | undefined
    descricao: string
  } = {
    setor: risco ? { label: risco.setor?.nome || '', value: risco.setor?.id as number } : undefined,
    cargo: risco ? { label: risco.cargo?.nome || '', value: risco.cargo?.id as number } : undefined,
    fatorRisco: risco
      ? { label: FatoresRiscosLabels[risco.fatorRisco], value: risco.fatorRisco }
      : undefined,
    descricao: risco?.descricao || '',
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: riscoSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (risco) {
          const risk: RiscoAsoDtoUpdate = {
            cargo: values.cargo?.value as number,
            setor: values.setor?.value as number,
            fatorRisco: values.fatorRisco?.value,
            descricao: values.descricao,
            ativo: risco.ativo,
          }
          await editRisco(risco.id, risk)
          showAlert({ title: 'Sucesso!', text: 'Risco alterado com sucesso!', icon: 'success' })
        } else {
          const risco: RiscoAsoDtoCreate = {
            cargo: values.cargo?.value as number,
            setor: values.setor?.value as number,
            fatorRisco: values.fatorRisco?.value as FatorRiscoType,
            descricao: values.descricao,
          }
          await createRisco(idCliente, risco)
          showAlert({ title: 'Sucesso!', text: 'Risco criado com sucesso!', icon: 'success' })
        }
        closeModal()
      } catch (error: any) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const extraParams = useMemo(
    () => ({ idEmpresa: Number(idEmpresa), idCliente: Number(idCliente) }),
    [idEmpresa, idCliente]
  )

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <FormSelect
        formik={formik}
        name='setor'
        label='Setor'
        placeholder='Selecione um setor'
        fetchOptions={OptionsHelper.getOptionsSetoresClientes}
        fetchOptionsParams={extraParams}
        isClearable={false}
        className='mb-3'
        isDisabled={formik.isSubmitting}
        isRequired
      />
      <FormSelect
        formik={formik}
        name='cargo'
        label='Cargo'
        placeholder='Selecione um cargo'
        fetchOptions={OptionsHelper.getOptionsCargosClientes}
        fetchOptionsParams={extraParams}
        isClearable={false}
        className='mb-3'
        isDisabled={formik.isSubmitting}
        isRequired
      />
      <FormSelect
        formik={formik}
        name='fatorRisco'
        label='Fator de Risco'
        placeholder='Selecione um fator de risco'
        fetchOptions={OptionsHelper.getOptionsFatoresRisco}
        isClearable={false}
        isDisabled={formik.isSubmitting}
        className='mb-3'
        isRequired
      />
      <FormInput
        formik={formik}
        name='descricao'
        label='Descrição'
        placeholder='Digite uma descrição do risco'
        isRequired={true}
      />

      <div className='d-flex justify-content-center mt-10'>
        <Button type={ButtonType.Reset} onClick={closeModal} className='btn-secondary me-3'>
          {t('common.cancel')}
        </Button>

        <Button type={ButtonType.Submit} className='btn-primary'>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.send')}
        </Button>
      </div>
    </form>
  )
}
